import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import apiClient from '@/services/api';
import type { MenuBreakdownReport } from '@/types/api';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import { SharePie } from '@/components/reports/SharePie';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Category / Group Sales: revenue grouped by menu group and by category. */
export default function CategoryGroupSalesReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<MenuBreakdownReport>({
    queryKey: ['menu-breakdown', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getMenuBreakdown(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const groups = data?.byGroup ?? [];
  const categories = data?.byCategory ?? [];
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const sections = (): ReportSection[] => [
    { heading: 'By Group', headers: ['Group', 'Qty', 'Revenue'], rows: groups.map((g) => [g.name, Number(g.quantity).toLocaleString(), formatCurrency(g.revenue)]) },
    { heading: 'By Category', headers: ['Category', 'Qty', 'Revenue'], rows: categories.map((c) => [c.name, Number(c.quantity).toLocaleString(), formatCurrency(c.revenue)]) },
  ];
  const kpis = (): [string, string][] => [
    ['Groups', groups.length.toLocaleString()],
    ['Categories', categories.length.toLocaleString()],
    ['Revenue', formatCurrency(data?.totals?.revenue ?? 0)],
  ];

  return (
    <ReportFrame
      title="Category / Group Sales"
      description="Revenue grouped by menu group and by category."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={groups.length === 0 && categories.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`category-group-sales-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Category / Group Sales', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Category / Group Sales Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card"><div className="card-body">
          <h3 className="text-lg font-semibold mb-4">Revenue by Group</h3>
          <SharePie data={groups.map((g) => ({ name: g.name, value: Number(g.revenue) }))} />
        </div></div>
        <div className="card"><div className="card-body">
          <h3 className="text-lg font-semibold mb-4">Revenue by Category</h3>
          <SharePie data={categories.map((c) => ({ name: c.name, value: Number(c.revenue) }))} />
        </div></div>
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">By Group</h3>
        <SimpleTable headers={['Group', 'Qty', 'Revenue']} rows={groups.map((g) => [g.name, Number(g.quantity).toLocaleString(), formatCurrency(g.revenue)])} />
      </div></div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">By Category</h3>
        <SimpleTable headers={['Category', 'Qty', 'Revenue']} rows={categories.map((c) => [c.name, Number(c.quantity).toLocaleString(), formatCurrency(c.revenue)])} />
      </div></div>
    </ReportFrame>
  );
}
